/**
 * Site Layout Component
 *
 * Provides the layout structure for site-specific views.
 * Displays site header with siteId and name, tab-based navigation,
 * and renders child routes via React Router Outlet.
 *
 * Requirements:
 * - 5.1: Display the siteId in the header when a site is successfully loaded
 * - 5.2: Display the site name in the header when a site is successfully loaded
 * - 5.3: Provide navigation to site-specific features (QR codes)
 * - 7.5: Tab-based navigation for site-specific views
 */

import { Navigate, NavLink, Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSite } from '../context/useSite';
import './SiteLayout.css';
import { useCallback, useEffect } from 'react';

/**
 * Back arrow icon for returning to site selection
 */
const BackIcon = () => (
  <svg className="site-header__back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
/**
 * Pending banner displayed in the site header for virtual sites.
 * Virtual sites have no real Toptracer Go enabled bays.
 *
 * Requirements:
 * - 4.1: Display banner adjacent to site info when isVirtual is true
 * - 4.3: Display label text "Pending"
 * - 4.5: Show tooltip on hover explaining the pending status
 * - 4.6: Do not display when isVirtual is false (handled by conditional render)
 */
/**
 * Pending banner displayed between the site header and tabs for virtual sites.
 * Virtual sites have no real Toptracer Go enabled bays.
 *
 * Requirements:
 * - 4.1: Display full-width banner between site header and site tabs when isVirtual is true
 * - 4.5: Explain the pending status clearly
 * - 4.6: Do not display when isVirtual is false (handled by conditional render)
 */
function PendingBanner(): JSX.Element {
  return (
    <div className="site-pending-banner" role="status">
      <span className="site-pending-banner__text">
          <b>WARNING:</b> This site does not (yet) have any bays enabled for Toptracer Go. <b>All bays shown are provisional.</b>
      </span>
    </div>
  );
}

/**
 * SiteLayout component that wraps site-specific views.
 *
 * Displays:
 * - Site header with siteId and name (Requirements 5.1, 5.2)
 * - Tab-based navigation (Requirements 5.3, 7.5)
 * - Child routes via Outlet
 *
 * Redirects to /site if no site is loaded.
 */
export function SiteLayout(): JSX.Element {
  const { siteId: urlSiteId } = useParams<{ siteId: string }>();
  const { site, isLoading, error, loadSite } = useSite();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if site has any bays (fixed or super)
  const hasBays = site ? site.bays.fixedBays.length > 0 || site.bays.superBays.length > 0 : false;

  // Get search string to preserve query params in navigation
  const searchString = searchParams.toString() ? `?${searchParams.toString()}` : '';

  // Load site from URL parameter if not already loaded or if URL changed
  useEffect(() => {
    if (urlSiteId && !isLoading && (!site || site.siteId !== urlSiteId)) {
      loadSite(urlSiteId);
    }
  }, [urlSiteId, site, isLoading, loadSite]);

  const handleBackClick = useCallback(() => {
    navigate('/site');
  }, [navigate]);

  // Redirect to site entry if there's an error (site not found, etc.)
  if (error) {
    return <Navigate to="/site" replace />;
  }

  // Redirect to site entry if no siteId in URL
  if (!urlSiteId) {
    return <Navigate to="/site" replace />;
  }

  // Show loading state while site is being loaded
  if (isLoading || !site) {
    return (
      <div className="site-layout">
        <div className="site-layout__loading">Loading site...</div>
      </div>
    );
  }

  return (
    <div className="site-layout">
      {/* Site Header - Requirements 5.1, 5.2 */}
      <header className="site-header">
        <button
          type="button"
          onClick={handleBackClick}
          className="site-header__back-link"
          title="Back to site selection"
        >
          <BackIcon />
        </button>
        <div className="site-header__info">
          <span className="site-header__site-id">{site.siteId}</span>
          <h1 className="site-header__site-name">{site.name}</h1>
        </div>
      </header>

      {/* Pending Banner - between header and tabs for virtual sites */}
      {site.isVirtual && <PendingBanner />}

      {/* Tab Navigation - Requirements 5.3, 7.5 */}
      <nav className="site-tabs" aria-label="Site navigation">
        <NavLink
          to={`/site/${site.siteId}${searchString}`}
          end
          className={({ isActive }) =>
            `site-tabs__tab${isActive ? ' site-tabs__tab--active' : ''}`
          }
        >
          Overview
        </NavLink>
        <NavLink
          to={hasBays ? `/site/${site.siteId}/customize-qr${searchString}` : '#'}
          onClick={(e) => !hasBays && e.preventDefault()}
          className={({ isActive }) =>
            `site-tabs__tab${isActive && hasBays ? ' site-tabs__tab--active' : ''}${!hasBays ? ' site-tabs__tab--disabled' : ''}`
          }
          aria-disabled={!hasBays}
          tabIndex={hasBays ? undefined : -1}
        >
          QR Codes
        </NavLink>
        {/*
          No hasBays gate: a club can be branded before any of its bays are
          enabled for Toptracer Go, so branding must not wait on them.
        */}
        <NavLink
          to={`/site/${site.siteId}/branding${searchString}`}
          className={({ isActive }) =>
            `site-tabs__tab${isActive ? ' site-tabs__tab--active' : ''}`
          }
        >
          Branding
        </NavLink>
        {/* No hasBays gate: feature flags belong to the site, not its bays. */}
        <NavLink
          to={`/site/${site.siteId}/features${searchString}`}
          className={({ isActive }) =>
            `site-tabs__tab${isActive ? ' site-tabs__tab--active' : ''}`
          }
        >
          Features
        </NavLink>
      </nav>

      {/* Main Content Area */}
      <main className="site-main">
        <Outlet />
      </main>
    </div>
  );
}
